// src/pages/OrderTrack.js
// LOT B : page publique de suivi via lien magique (token).
// L'acheteur (même invité, sans compte) peut CONFIRMER la réception
// ou SIGNALER un problème. Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiTruck, FiPackage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { trackAPI } from '../services/api';
import DeliveryTimeline from '../components/DeliveryTimeline';

const DELIVERY = {
  pending: { label: 'En préparation', cls: 'bg-gray-100 text-gray-700', Icon: FiPackage },
  accepted: { label: 'Acceptée', cls: 'bg-blue-50 text-blue-700', Icon: FiPackage },
  prepared: { label: 'Préparée', cls: 'bg-blue-50 text-blue-700', Icon: FiPackage },
  shipped: { label: 'Expédiée', cls: 'bg-indigo-50 text-indigo-700', Icon: FiTruck },
  delivered: { label: 'Livrée', cls: 'bg-amber-50 text-amber-700', Icon: FiTruck },
  received: { label: 'Réceptionnée', cls: 'bg-green-50 text-green-700', Icon: FiCheckCircle },
};
const REASONS = [
  { value: 'not_received', label: 'Non reçu' },
  { value: 'damaged', label: 'Endommagé' },
  { value: 'not_as_described', label: 'Non conforme à la description' },
  { value: 'other', label: 'Autre' },
];
const fmt = (v) => (Number(v) || 0).toFixed(2);

const OrderTrack = () => {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const t = params.get('t') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [reason, setReason] = useState('not_received');
  const [desc, setDesc] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await trackAPI.get(orderNumber, t);
      setData(res.data?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Lien invalide ou expiré');
    } finally { setLoading(false); }
  }, [orderNumber, t]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await trackAPI.confirm(orderNumber, t);
      setData(res.data?.data || data);
      toast.success(res.data?.message || 'Réception confirmée, merci !');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la confirmation');
    } finally { setBusy(false); }
  };

  const submitDispute = async () => {
    setBusy(true);
    try {
      const res = await trackAPI.dispute(orderNumber, t, { reason, description: desc });
      setData(res.data?.data || data);
      setShowDispute(false); setDesc('');
      toast.success(res.data?.message || 'Réclamation enregistrée');
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="max-w-lg mx-auto px-4 py-16 text-center text-gray-400">Chargement…</div>;
  }
  if (err || !data) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <FiAlertCircle className="mx-auto text-red-500 mb-3" size={40} />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
        <p className="text-gray-500 mb-6">{err || "Cette commande est introuvable."}</p>
        <Link to="/" className="text-primary-600 font-semibold hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  const d = DELIVERY[data.deliveryStatus] || DELIVERY.pending;
  const DIcon = d.Icon;
  const canAct = data.paymentStatus === 'completed' && data.status !== 'cancelled';
  const isDelivered = data.deliveryStatus === 'delivered';
  const isReceived = data.deliveryStatus === 'received';

  return (
    <div className="max-w-lg mx-auto px-4 py-10">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-gray-400">Commande</p>
            <p className="font-mono font-bold text-gray-900">{data.orderNumber}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${d.cls}`}>
            <DIcon size={15} /> {d.label}
          </span>
        </div>

        <div className="border-t border-gray-100 py-4 space-y-2">
          {(data.items || []).map((it, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-gray-700">{it.title} <span className="text-gray-400">× {it.quantity}</span></span>
              <span className="text-gray-600">{fmt(it.price)} MAD</span>
            </div>
          ))}
          <div className="flex justify-between font-semibold pt-2 border-t border-gray-100">
            <span>Total</span><span>{fmt(data.total)} MAD</span>
          </div>
        </div>

        {(data.deliveryStatus || data.carrier || data.trackingNumber) && (
          <div className="border-t border-gray-100 pt-4 mb-2">
            <DeliveryTimeline order={data} productType={data.isDigital ? 'digital' : 'physical'} />
          </div>
        )}

        {isReceived && (
          <div className="mt-4 bg-green-50 border border-green-100 rounded-xl p-4 text-center">
            <FiCheckCircle className="mx-auto text-green-600 mb-1" size={26} />
            <p className="font-semibold text-green-800">Réception confirmée</p>
            <p className="text-sm text-green-700">Merci, cette commande est clôturée.</p>
          </div>
        )}

        {data.hasDispute && (
          <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-800">
            Une réclamation est en cours sur cette commande (statut : {data.disputeStatus}). Notre équipe la traite.
          </div>
        )}

        {canAct && isDelivered && !data.hasDispute && (
          <div className="mt-5 space-y-3">
            <button onClick={confirm} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">
              <FiCheckCircle size={18} /> {busy ? 'Confirmation…' : "J'ai bien reçu ma commande"}
            </button>
            <button onClick={() => setShowDispute(true)} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50">
              <FiAlertCircle size={18} /> Signaler un problème
            </button>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">Maxula — suivi de commande sécurisé</p>

      {showDispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Signaler un problème</h3>
            <p className="text-sm text-gray-500 mb-4">Commande <span className="font-mono font-semibold">{data.orderNumber}</span></p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-200">
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Décrivez le problème</label>
            <textarea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="Expliquez-nous ce qui s'est passé…"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-primary-200" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDispute(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Annuler</button>
              <button onClick={submitDispute} disabled={busy}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                {busy ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrack;
